/**
 * CoachPlaybook - Basketball Court Component
 * Version: 0.6.0
 * 
 * v0.5.134 Changes:
 * - Restored PNG court images (court-half.png, court-full.png)
 * 
 * v0.5.98 Changes:
 * - Players without actions fade to 40% opacity
 * - Screen rectangle stays fully dark while in place (not faded)
 * - Removed shot arc line, kept animated ball only
 */

import React, { useRef, useState, useCallback, useEffect } from 'react';
import PlaySurfaceRenderer from './PlaySurfaceRenderer';

const COURT_HALF = '/court-half.png';
const COURT_FULL = '/court-full.png';
const HOOP_POSITION = { x: 50, y: 10.5 };

// Sport-specific ball emoji mapping
const SPORT_BALL_EMOJI = {
  basketball: '\u{1F3C0}',  // 🏀
  soccer: '\u26BD',         // ⚽
  football: '\u{1F3C8}',    // 🏈
  baseball: '\u26BE',       // ⚾
  volleyball: '\u{1F3D0}',  // 🏐
};

const getBallEmoji = (sportId) => SPORT_BALL_EMOJI[sportId] || '\u{1F3C0}';

// ============================================
// SNAP GRID SYSTEM (1000×1000 virtual grid)
// ============================================
const GRID = {
  size: 1000,           // Virtual grid size
  divisions: 100,       // 100×100 = 10,000 snap points
  courtWidth: 100,      // ViewBox width
  courtHeight: 100,     // ViewBox height
  snapEnabled: true     // Global snap toggle
};

// Convert grid coordinates to court coordinates
const gridToCoord = (gridX, gridY) => ({
  x: (gridX / GRID.size) * GRID.courtWidth,
  y: (gridY / GRID.size) * GRID.courtHeight
});

// Convert court coordinates to grid coordinates
const coordToGrid = (x, y) => ({
  gridX: Math.round((x / GRID.courtWidth) * GRID.size),
  gridY: Math.round((y / GRID.courtHeight) * GRID.size)
});

// Snap to nearest grid point
const snapToGrid = (x, y, enabled = GRID.snapEnabled) => {
  if (!enabled) return { x, y };
  
  const cellSize = GRID.size / GRID.divisions; // 10 units per cell
  const grid = coordToGrid(x, y);
  
  // Round to nearest grid intersection
  const snappedGridX = Math.round(grid.gridX / cellSize) * cellSize;
  const snappedGridY = Math.round(grid.gridY / cellSize) * cellSize;
  
  return gridToCoord(snappedGridX, snappedGridY);
};

// Snap array of points
const snapPointsToGrid = (points, enabled = GRID.snapEnabled) => {
  if (!enabled || !points) return points;
  return points.map(p => snapToGrid(p.x, p.y, enabled));
};

// CSS Animations
const courtStyles = `
  @keyframes ballPulse1 {
    0%, 100% { opacity: 1; }
    33%, 66% { opacity: 0.2; }
  }
  @keyframes ballPulse2 {
    0%, 33%, 100% { opacity: 0.2; }
    50% { opacity: 1; }
  }
  @keyframes ballPulse3 {
    0%, 66% { opacity: 0.2; }
    83% { opacity: 1; }
  }
  @keyframes starPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }
  @keyframes ballHolderGlow {
    0%, 100% { filter: drop-shadow(0 0 2px rgba(255,165,0,0.8)); }
    50% { filter: drop-shadow(0 0 5px rgba(255,165,0,1)); }
  }
  
  /* Sequential fill animations for movement chevrons */
  @keyframes fillChevron1of4 {
    0%, 83.3% { opacity: 0.9; }
    83.4%, 100% { opacity: 0.2; }
  }
  @keyframes fillChevron2of4 {
    0%, 16.6% { opacity: 0.2; }
    16.7%, 83.3% { opacity: 0.9; }
    83.4%, 100% { opacity: 0.2; }
  }
  @keyframes fillChevron3of4 {
    0%, 33.2% { opacity: 0.2; }
    33.3%, 83.3% { opacity: 0.9; }
    83.4%, 100% { opacity: 0.2; }
  }
  @keyframes fillChevron4of4 {
    0%, 49.9% { opacity: 0.2; }
    50%, 83.3% { opacity: 0.9; }
    83.4%, 100% { opacity: 0.2; }
  }
  
  @keyframes fillChevron1of3 {
    0%, 90% { opacity: 0.9; }
    90.1%, 100% { opacity: 0.2; }
  }
  @keyframes fillChevron2of3 {
    0%, 19.9% { opacity: 0.2; }
    20%, 90% { opacity: 0.9; }
    90.1%, 100% { opacity: 0.2; }
  }
  @keyframes fillChevron3of3 {
    0%, 39.9% { opacity: 0.2; }
    40%, 90% { opacity: 0.9; }
    90.1%, 100% { opacity: 0.2; }
  }
  
  @keyframes fillChevron1of2 {
    0%, 85% { opacity: 0.9; }
    85.1%, 100% { opacity: 0.2; }
  }
  @keyframes fillChevron2of2 {
    0%, 29.9% { opacity: 0.2; }
    30%, 85% { opacity: 0.9; }
    85.1%, 100% { opacity: 0.2; }
  }
  
  @keyframes fillChevron1of1 {
    0%, 60% { opacity: 0.9; }
    60.1%, 100% { opacity: 0.2; }
  }
  
  .pass-ball-1 { animation: ballPulse1 1.5s ease-in-out infinite; }
  .pass-ball-2 { animation: ballPulse2 1.5s ease-in-out infinite; }
  .pass-ball-3 { animation: ballPulse3 1.5s ease-in-out infinite; }
  .shot-star { animation: starPulse 0.8s ease-in-out infinite; }
  .ball-holder { animation: ballHolderGlow 1.5s ease-in-out infinite; }
  
  .chevron-1-of-4 { animation: fillChevron1of4 2.4s ease-in-out infinite; }
  .chevron-2-of-4 { animation: fillChevron2of4 2.4s ease-in-out infinite; }
  .chevron-3-of-4 { animation: fillChevron3of4 2.4s ease-in-out infinite; }
  .chevron-4-of-4 { animation: fillChevron4of4 2.4s ease-in-out infinite; }
  
  .chevron-1-of-3 { animation: fillChevron1of3 2.0s ease-in-out infinite; }
  .chevron-2-of-3 { animation: fillChevron2of3 2.0s ease-in-out infinite; }
  .chevron-3-of-3 { animation: fillChevron3of3 2.0s ease-in-out infinite; }
  
  .chevron-1-of-2 { animation: fillChevron1of2 1.8s ease-in-out infinite; }
  .chevron-2-of-2 { animation: fillChevron2of2 1.8s ease-in-out infinite; }
  
  .chevron-1-of-1 { animation: fillChevron1of1 1.6s ease-in-out infinite; }
`;

// Catmull-Rom spline for smooth curves through points
const smoothPath = (points) => {
  if (!points || points.length < 2) return '';
  if (points.length === 2) {
    return `M ${points[0].x},${points[0].y} L ${points[1].x},${points[1].y}`;
  }
  
  let d = `M ${points[0].x},${points[0].y}`;
  
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(points.length - 1, i + 2)];
    
    const tension = 6;
    const cp1x = p1.x + (p2.x - p0.x) / tension;
    const cp1y = p1.y + (p2.y - p0.y) / tension;
    const cp2x = p2.x - (p3.x - p1.x) / tension;
    const cp2y = p2.y - (p3.y - p1.y) / tension;
    
    d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
  }
  
  return d;
};

// Simplify path by removing points too close together
// v0.4.9: Improved path smoothing for shaky hand-drawn paths
// Uses Ramer-Douglas-Peucker for simplification + Chaikin smoothing
const simplifyPath = (points, minDistance = 3) => {
  if (!points || points.length < 2) return points;
  
  // Step 1: Ramer-Douglas-Peucker simplification
  const rdpSimplify = (pts, epsilon) => {
    if (pts.length < 3) return pts;
    
    // Find the point with maximum distance from line between first and last
    let maxDist = 0;
    let maxIdx = 0;
    const first = pts[0];
    const last = pts[pts.length - 1];
    
    for (let i = 1; i < pts.length - 1; i++) {
      const dist = perpendicularDistance(pts[i], first, last);
      if (dist > maxDist) {
        maxDist = dist;
        maxIdx = i;
      }
    }
    
    // If max distance is greater than epsilon, recursively simplify
    if (maxDist > epsilon) {
      const left = rdpSimplify(pts.slice(0, maxIdx + 1), epsilon);
      const right = rdpSimplify(pts.slice(maxIdx), epsilon);
      return [...left.slice(0, -1), ...right];
    } else {
      return [first, last];
    }
  };
  
  // Perpendicular distance from point to line
  const perpendicularDistance = (point, lineStart, lineEnd) => {
    const dx = lineEnd.x - lineStart.x;
    const dy = lineEnd.y - lineStart.y;
    const lineLengthSq = dx * dx + dy * dy;
    
    if (lineLengthSq === 0) {
      return Math.sqrt((point.x - lineStart.x) ** 2 + (point.y - lineStart.y) ** 2);
    }
    
    const t = Math.max(0, Math.min(1, ((point.x - lineStart.x) * dx + (point.y - lineStart.y) * dy) / lineLengthSq));
    const projX = lineStart.x + t * dx;
    const projY = lineStart.y + t * dy;
    
    return Math.sqrt((point.x - projX) ** 2 + (point.y - projY) ** 2);
  };
  
  // Step 2: Apply RDP with tolerance based on path length
  const pathLength = Math.sqrt(
    (points[points.length - 1].x - points[0].x) ** 2 + 
    (points[points.length - 1].y - points[0].y) ** 2
  );
  const epsilon = Math.max(1, pathLength * 0.03); // 3% of path length as tolerance
  let simplified = rdpSimplify(points, epsilon);
  
  // Step 3: Chaikin corner cutting for smoothness (1 iteration)
  // Only if we have enough points
  if (simplified.length > 2) {
    const smoothed = [simplified[0]]; // Keep start point
    for (let i = 0; i < simplified.length - 1; i++) {
      const p0 = simplified[i];
      const p1 = simplified[i + 1];
      
      // Add points at 25% and 75% along each segment
      if (i > 0) { // Skip first segment's first point
        smoothed.push({
          x: p0.x * 0.75 + p1.x * 0.25,
          y: p0.y * 0.75 + p1.y * 0.25
        });
      }
      if (i < simplified.length - 2) { // Skip last segment's last point  
        smoothed.push({
          x: p0.x * 0.25 + p1.x * 0.75,
          y: p0.y * 0.25 + p1.y * 0.75
        });
      }
    }
    smoothed.push(simplified[simplified.length - 1]); // Keep end point
    simplified = smoothed;
  }
  
  // Step 4: Final pass - remove points too close together
  const result = [simplified[0]];
  for (let i = 1; i < simplified.length - 1; i++) {
    const last = result[result.length - 1];
    const dist = Math.sqrt((simplified[i].x - last.x) ** 2 + (simplified[i].y - last.y) ** 2);
    if (dist >= minDistance) {
      result.push(simplified[i]);
    }
  }
  result.push(simplified[simplified.length - 1]); // Always keep end point
  
  return result;
};

const hexToRgba = (hex, alpha = 1) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return hex;
  return `rgba(${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(result[3], 16)},${alpha})`;
};

// Calculate path length using Pythagorean theorem
const getPathLength = (points) => {
  if (!points || points.length < 2) return 0;
  let length = 0;
  for (let i = 1; i < points.length; i++) {
    const dx = points[i].x - points[i - 1].x;
    const dy = points[i].y - points[i - 1].y;
    length += Math.sqrt(dx * dx + dy * dy);
  }
  return length;
};

// Get chevron positions along path
// Short (<20): 2 at 45%, 85%
// Medium (20-40): 3 at 20%, 50%, 85%
// Long (>40): 4 at 15%, 40%, 65%, 90%
const getChevronPositions = (points) => {
  if (!points || points.length < 2) return [];
  
  const totalLength = getPathLength(points);
  if (totalLength < 8) return [];
  
  let positions;
  if (totalLength < 20) {
    positions = [0.45, 0.85];
  } else if (totalLength < 40) {
    positions = [0.20, 0.50, 0.85];
  } else {
    positions = [0.15, 0.40, 0.65, 0.90];
  }
  
  const result = [];
  
  for (const targetFraction of positions) {
    const targetDist = totalLength * targetFraction;
    let currentDist = 0;
    
    for (let i = 1; i < points.length; i++) {
      const dx = points[i].x - points[i - 1].x;
      const dy = points[i].y - points[i - 1].y;
      const segmentLength = Math.sqrt(dx * dx + dy * dy);
      
      if (currentDist + segmentLength >= targetDist) {
        const t = (targetDist - currentDist) / segmentLength;
        const x = points[i - 1].x + dx * t;
        const y = points[i - 1].y + dy * t;
        const angle = Math.atan2(dy, dx) * (180 / Math.PI);
        result.push({ x, y, angle });
        break;
      }
      currentDist += segmentLength;
    }
  }
  
  return result;
};

// Get point along a straight line at position t (0-1)
const getPointOnLine = (from, to, t) => ({
  x: from.x + (to.x - from.x) * t,
  y: from.y + (to.y - from.y) * t,
  angle: Math.atan2(to.y - from.y, to.x - from.x) * (180 / Math.PI)
});

const BasketballCourt = ({
  courtView = 'half',
  positions = [],
  paths = [],
  screens = [],
  passes = [],
  shots = [],
  defenders = [],
  defenderPaths = [],  // v0.4.6: Defender movement paths
  selectedPosition = null,
  selectedDefender = null,  // v0.5.71: Selected defender ID
  selectedPath = null,
  placementMode = null,
  drawingMode = null,
  tapMode = null,
  currentStep = 0,
  animationProgress = 0,
  isAnimating = false,
  // v0.4.0 new props
  showGrid = false,           // Toggle debug grid visibility
  spotlightPlayer = null,     // Position ID to spotlight (others become watermarks)
  snapEnabled = true,         // Enable/disable snap to grid
  showJerseys = true,         // v0.5.71: Show jersey shapes (default ON)
  jerseyTemplate = null,      // v0.5.71: Team jersey template for colors
  defenderJerseyColor = '#dc2626', // v0.5.71: Defender jersey fill color
  sport = 'basketball', // v0.6.0: Sport ID for court surface rendering
  lineColors,
  onCourtClick, onPlayerClick, onPlayerDrag, onDefenderClick, onPathStart, onPathPoint, onPathEnd, onDefenderPathEnd, onScreenPlace, onScreenPathEnd, onPathSelect, onPathPointDrag, onDefenderDrag, onScreenDrag, onSpotlightChange
}) => {
  const imageRef = useRef(null);
  const justFinishedDragRef = useRef(false);  // v0.5.82: Prevent click after drag
  const [cursorPosition, setCursorPosition] = useState(null);
  const [draggingPlayer, setDraggingPlayer] = useState(null);
  const [draggingDefender, setDraggingDefender] = useState(null);
  const [draggingScreen, setDraggingScreen] = useState(null);  // v0.4.6: Screen dragging
  const [draggingPathPoint, setDraggingPathPoint] = useState(null);
  const [isDrawingPath, setIsDrawingPath] = useState(false);
  const [currentPath, setCurrentPath] = useState([]);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const courtImage = courtView === 'half' ? COURT_HALF : COURT_FULL;
  
  // v0.4.0: Smaller default player icons
  const GHOST_RADIUS = 3.0;  // Was 3.5
  const BASE_RADIUS = 2.5;   // Was 3.2
  
  useEffect(() => { setImageLoaded(false); }, [courtView]);
  
  // v0.4.0: Snap-aware coordinate conversion
  const screenToCoord = useCallback((clientX, clientY) => {
    if (!imageRef.current) return null;
    const rect = imageRef.current.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;
    if (x < 0 || x > 100 || y < 0 || y > 100) return null;
    
    // Apply snap grid
    return snapToGrid(x, y, snapEnabled);
  }, [snapEnabled]);
  
  const getPlayerPositionAtStep = (positionId, atStep) => {
    const pos = positions.find(p => p.id === positionId);
    if (!pos || pos.startX === null) return null;
    let currentX = pos.startX, currentY = pos.startY;
    paths.filter(p => p.positionId === positionId && p.stepNumber <= atStep && p.points?.length >= 2)
      .sort((a, b) => a.stepNumber - b.stepNumber)
      .forEach(path => { currentX = path.points[path.points.length - 1].x; currentY = path.points[path.points.length - 1].y; });
    return { x: currentX, y: currentY };
  };
  
  const getPlayerInfo = (positionId) => positions.find(p => p.id === positionId);
  
  const getBallHolderAtStep = (atStep) => {
    let ballHolder = positions.find(p => p.hasBall)?.id || positions[0]?.id;
    const events = [];
    paths.filter(p => p.type === 'dribble' && p.stepNumber <= atStep).forEach(p => events.push({ step: p.stepNumber, holder: p.positionId }));
    passes.filter(p => p.stepNumber <= atStep).forEach(p => events.push({ step: p.stepNumber, holder: p.toPositionId }));
    events.sort((a, b) => a.step - b.step).forEach(e => { ballHolder = e.holder; });
    return ballHolder;
  };
  
  // v0.5.20: Get animated position with per-player speed support
  // v0.5.62: When not animating, show position at END of completed paths (not start)
  const getAnimatedPosition = (pos) => {
    if (!pos || pos.startX === null) return null;
    
    // v0.5.62: Step 0 always shows starting positions
    if (currentStep === 0) {
      return { x: pos.startX, y: pos.startY };
    }
    
    // v0.5.62: When NOT animating (design mode), show position at end of paths up to currentStep
    // This ensures players appear at their destination after a path is drawn
    if (!isAnimating) {
      return getPlayerPositionAtStep(pos.id, currentStep) || { x: pos.startX, y: pos.startY };
    }
    
    // v0.5.20: Apply player-specific speed to animation progress
    const playerSpeed = pos.speed || 1.0;
    const adjustedProgress = Math.min(1, animationProgress * playerSpeed);
    
    const basePos = getPlayerPositionAtStep(pos.id, currentStep - 1) || { x: pos.startX, y: pos.startY };
    const currentStepPath = paths.find(p => p.positionId === pos.id && p.stepNumber === currentStep && p.points?.length >= 2);
    
    if (!currentStepPath) {
      // If no path for current step, check if this player already finished (fast player)
      if (playerSpeed > 1 && adjustedProgress >= 1) {
        return getPlayerPositionAtStep(pos.id, currentStep) || basePos;
      }
      return currentStep > 0 ? getPlayerPositionAtStep(pos.id, currentStep) || basePos : basePos;
    }
    
    const pts = currentStepPath.points;
    const idx = adjustedProgress * (pts.length - 1);
    const lo = Math.floor(idx);
    const hi = Math.min(lo + 1, pts.length - 1);
    const t = idx - lo;
    return { x: pts[lo].x + (pts[hi].x - pts[lo].x) * t, y: pts[lo].y + (pts[hi].y - pts[lo].y) * t };
  };
  
  // v0.5.62: Get guarded player's position at a specific step (for defender calibration)
  const getGuardedPlayerPosition = (guardingPlayer, atStep) => {
    if (!guardingPlayer) return null;
    if (atStep === 0) {
      return { x: guardingPlayer.startX, y: guardingPlayer.startY };
    }
    return getPlayerPositionAtStep(guardingPlayer.id, atStep) || { x: guardingPlayer.startX, y: guardingPlayer.startY };
  };
  
  const handleCourtClick = (e) => {
    // v0.5.82: Skip click if we just finished dragging (prevents auto-placing defender)
    if (justFinishedDragRef.current) {
      console.log('[BasketballCourt] Skipping click - just finished drag');
      return;
    }
    
    const coord = screenToCoord(e.clientX, e.clientY);
    if (!coord) return;
    // v0.5.72: defenderEndPosition mode - single click to set end position
    if (drawingMode?.type === 'defenderEndPosition' && onCourtClick) {
      onCourtClick(coord.x, coord.y);
      return;
    }
    if (drawingMode?.type === 'screen' && onScreenPlace) onScreenPlace(coord.x, coord.y);
    else if (onCourtClick) onCourtClick(coord.x, coord.y);
  };
  
  const handleMouseDown = (e) => {
    // v0.5.72: Don't start drawing for defenderEndPosition mode (uses click instead)
    if (drawingMode?.type === 'defenderEndPosition') {
      return;
    }
    // v0.5.71: Handle defender path drawing mode
    if (drawingMode?.type === 'defenderPath' && drawingMode.defenderId) {
      const coord = screenToCoord(e.clientX, e.clientY);
      if (coord) { setIsDrawingPath(true); setCurrentPath([coord]); onPathStart?.(drawingMode.defenderId, coord); }
      return;
    }
    // v0.5.1: Handle both path and screen drawing modes
    if ((drawingMode?.type === 'path' || drawingMode?.type === 'screen') && drawingMode.positionId) {
      const coord = screenToCoord(e.clientX, e.clientY);
      if (coord) { setIsDrawingPath(true); setCurrentPath([coord]); onPathStart?.(drawingMode.positionId, coord); }
    }
  };
  
  const handleMouseMove = (e) => {
    const coord = screenToCoord(e.clientX, e.clientY);
    setCursorPosition(coord);
    // v0.5.71: Handle defender path drawing
    if (coord && isDrawingPath && drawingMode?.type === 'defenderPath') {
      const last = currentPath[currentPath.length - 1];
      if (last && Math.sqrt((coord.x - last.x) ** 2 + (coord.y - last.y) ** 2) > 2) {
        setCurrentPath(prev => [...prev, coord]); onPathPoint?.(coord);
      }
    }
    // v0.5.1: Handle both path and screen drawing modes
    else if (coord && isDrawingPath && (drawingMode?.type === 'path' || drawingMode?.type === 'screen')) {
      const last = currentPath[currentPath.length - 1];
      if (last && Math.sqrt((coord.x - last.x) ** 2 + (coord.y - last.y) ** 2) > 2) {
        setCurrentPath(prev => [...prev, coord]); onPathPoint?.(coord);
      }
    }
    if (coord && draggingPathPoint && onPathPointDrag) onPathPointDrag(draggingPathPoint.pathId, draggingPathPoint.pointIndex, coord.x, coord.y);
    if (coord && draggingPlayer && onPlayerDrag) onPlayerDrag(draggingPlayer, coord.x, coord.y);
    if (coord && draggingDefender && onDefenderDrag) onDefenderDrag(draggingDefender, coord.x, coord.y);
    if (coord && draggingScreen && onScreenDrag) onScreenDrag(draggingScreen, coord.x, coord.y);  // v0.4.6
  };
  
  const handleTouchStart = (e) => { if (e.touches.length === 1) handleMouseDown({ clientX: e.touches[0].clientX, clientY: e.touches[0].clientY }); };
  const handleTouchMove = (e) => { if (e.touches.length === 1) { e.preventDefault(); handleMouseMove({ clientX: e.touches[0].clientX, clientY: e.touches[0].clientY }); } };
  
  const handleMouseUp = () => {
    if (isDrawingPath && currentPath.length > 1) {
      // v0.5.82: Mark that we just finished dragging - skip next click event
      justFinishedDragRef.current = true;
      setTimeout(() => { justFinishedDragRef.current = false; }, 100);
      
      // v0.5.71: Route defender path to correct handler
      if (drawingMode?.type === 'defenderPath' && onDefenderPathEnd) {
        onDefenderPathEnd(drawingMode.defenderId, simplifyPath(currentPath, 5));
      }
      // v0.5.1: Route to correct handler based on drawing mode
      else if (drawingMode?.type === 'screen' && onScreenPathEnd) {
        onScreenPathEnd(drawingMode.positionId, simplifyPath(currentPath, 5));
      } else if (onPathEnd) {
        onPathEnd(drawingMode.positionId, simplifyPath(currentPath, 5), drawingMode.pathType || 'movement');
      }
    }
    setIsDrawingPath(false); setCurrentPath([]); setDraggingPlayer(null); setDraggingDefender(null); setDraggingScreen(null); setDraggingPathPoint(null);
  };
  
  const placedPositions = positions.filter(p => p.startX !== null && p.startY !== null);
  const validPaths = paths.filter(p => p?.points?.length >= 2);
  const validPasses = passes.filter(p => p?.from && p?.to);
  const validScreens = screens.filter(s => s && typeof s.x === 'number');
  const validShots = shots || [];
  
  // Ball holder calculation
  // v0.5.48: At step 0, show initial ball holder (not final state)
  const maxStep = Math.max(0, ...paths.map(p => p.stepNumber || 1), ...passes.map(p => p.stepNumber || 1), ...screens.map(s => s.stepNumber || 1), ...(shots || []).map(s => s.stepNumber || 1));
  const ballHolderStep = (currentStep === 0 || isAnimating) ? currentStep : maxStep;
  const currentBallHolder = getBallHolderAtStep(ballHolderStep);
  
  const getArrowId = (color) => `arrow-${color.replace('#', '')}`;
  // v0.5.71: Include defender red color for arrow markers
  const playerColors = [...new Set([...positions.map(p => p.color).filter(Boolean), '#dc2626'])];
  
  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px', boxSizing: 'border-box' }}>
      <style>{courtStyles}</style>
      <div style={{ position: 'relative', display: 'inline-block', border: '1px solid #9ca3af', padding: '4px', backgroundColor: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', cursor: drawingMode || placementMode ? 'crosshair' : 'default', maxWidth: '100%', maxHeight: '100%' }}
        onClick={handleCourtClick} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleMouseUp}>
        <div style={{ position: 'relative', overflow: 'hidden', lineHeight: 0 }}>
          {sport === 'basketball' ? (
            <img ref={imageRef} src={courtImage} alt="Basketball court" onLoad={() => setImageLoaded(true)} style={{ display: 'block', width: 'auto', height: '400px', maxWidth: '100%', objectFit: 'contain', border: 'none', margin: 0, padding: 0 }} draggable={false} />
          ) : (
            <div ref={imageRef} style={{ width: '400px', maxWidth: '100%' }}>
              <PlaySurfaceRenderer sport={sport} view={courtView || 'full'} width={400} />
              {(() => { if (!imageLoaded) setTimeout(() => setImageLoaded(true), 50); return null; })()}
            </div>
          )}
          
          {imageLoaded && (
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
              <defs>
                {playerColors.map(color => (
                  <marker key={color} id={getArrowId(color)} markerWidth="4" markerHeight="4" refX="3.5" refY="2" orient="auto" markerUnits="userSpaceOnUse">
                    <path d="M0,0.5 L3.5,2 L0,3.5" fill="none" stroke={color} strokeWidth="0.8" />
                  </marker>
                ))}
                <marker id="arrow-gray" markerWidth="4" markerHeight="4" refX="3.5" refY="2" orient="auto" markerUnits="userSpaceOnUse">
                  <path d="M0,0.5 L3.5,2 L0,3.5" fill="none" stroke="#374151" strokeWidth="0.8" />
                </marker>
              </defs>
              
              {/* v0.4.0: Debug Grid (toggleable) */}
              {showGrid && (
                <g className="debug-grid" opacity="0.15">
                  {/* Vertical lines every 10 units (10 divisions) */}
                  {Array.from({ length: 11 }, (_, i) => (
                    <line key={`v-${i}`} x1={i * 10} y1={0} x2={i * 10} y2={100} stroke="#666" strokeWidth="0.15" />
                  ))}
                  {/* Horizontal lines every 10 units */}
                  {Array.from({ length: 11 }, (_, i) => (
                    <line key={`h-${i}`} x1={0} y1={i * 10} x2={100} y2={i * 10} stroke="#666" strokeWidth="0.15" />
                  ))}
                  {/* Finer grid every 5 units */}
                  {Array.from({ length: 21 }, (_, i) => (
                    <line key={`vf-${i}`} x1={i * 5} y1={0} x2={i * 5} y2={100} stroke="#999" strokeWidth="0.05" />
                  ))}
                  {Array.from({ length: 21 }, (_, i) => (
                    <line key={`hf-${i}`} x1={0} y1={i * 5} x2={100} y2={i * 5} stroke="#999" strokeWidth="0.05" />
                  ))}
                  {/* Center crosshair */}
                  <circle cx={50} cy={50} r={0.5} fill="#f00" opacity="0.5" />
                </g>
              )}
              
              {/* Movement Paths */}
              {validPaths.map((path) => {
                const stepNum = path.stepNumber || 1;
                
                // v0.5.64: Path history visibility - works in both animation AND design mode
                // Previous steps: faded (completed paths)
                // Current step: bright (active/focused)
                // Future steps: hidden
                let pathOpacity = 0.85;
                let showPath = true;
                let isFocusedPath = false; // Track if this is the "current" step
                
                // Hide all paths at step 0 (initial positions view)
                if (currentStep === 0) {
                  showPath = false;
                } else {
                  // v0.5.64: Apply step-based visibility in BOTH animation and design mode
                  if (stepNum < currentStep) {
                    pathOpacity = 0.3; // Previous steps - faded history
                  } else if (stepNum === currentStep) {
                    pathOpacity = 1.0; // Current step - bright/focused
                    isFocusedPath = true;
                  } else {
                    showPath = false; // Future steps - hidden
                  }
                }
                
                // v0.4.0: Spotlight mode - dim non-spotlit players' paths
                if (spotlightPlayer && path.positionId !== spotlightPlayer) {
                  pathOpacity *= 0.25;
                }
                
                if (!showPath) return null;
                
                const isSelected = selectedPath === path.id;
                const player = getPlayerInfo(path.positionId);
                const playerColor = player?.color || '#374151';
                const isScreenMove = path.isScreenMove;
                const pathType = path.type || 'movement';
                
                const startPoint = path.points[0];
                const endPoint = path.points[path.points.length - 1];
                
                // Offset path start from ghost circle edge
                let offsetStartPoint = startPoint;
                if (path.points.length >= 2) {
                  const nextPoint = path.points[1];
                  const dx = nextPoint.x - startPoint.x;
                  const dy = nextPoint.y - startPoint.y;
                  const dist = Math.sqrt(dx * dx + dy * dy);
                  if (dist > 0) {
                    offsetStartPoint = {
                      x: startPoint.x + (dx / dist) * GHOST_RADIUS,
                      y: startPoint.y + (dy / dist) * GHOST_RADIUS
                    };
                  }
                }
                
                const offsetPoints = [offsetStartPoint, ...path.points.slice(1)];
                const d = smoothPath(offsetPoints);
                
                // Chevrons for all movement paths (including screen moves)
                const chevronPositions = getChevronPositions(offsetPoints);
                const chevronCount = chevronPositions.length;
                
                return (
                  <g key={path.id} opacity={pathOpacity}>
                    {/* Ghost circle at start - shows where player started this step */}
                    <g>
                      <circle cx={startPoint.x} cy={startPoint.y} r={GHOST_RADIUS} fill="none" stroke={playerColor} strokeWidth="0.5" strokeDasharray="2,1" opacity="0.5"/>
                      <text x={startPoint.x} y={startPoint.y + 0.3} textAnchor="middle" dominantBaseline="middle" fill={playerColor} fontSize="2.2" fontWeight="bold" opacity="0.4">{player?.positionLabel || ''}</text>
                    </g>
                    
                    {isScreenMove ? (
                      <>
                        {/* v0.5.41: Screen move uses same dotted style as regular movement */}
                        {/* White outline for visibility */}
                        <path d={d} fill="none" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" opacity="0.5" />
                        
                        {/* Main path line - dotted like movement */}
                        <path d={d} fill="none" stroke={playerColor} strokeWidth="0.6" strokeLinecap="round" strokeLinejoin="round" 
                          strokeDasharray="2,1.5"
                          opacity="0.85"
                          style={{ pointerEvents: 'stroke', cursor: 'pointer' }} 
                          onClick={(e) => { e.stopPropagation(); onPathSelect?.(path.id); }} 
                        />
                        
                        {/* Chevrons - same style as movement paths */}
                        {chevronPositions.map((pt, idx) => (
                          <g key={`chevron-${idx}`} className={`chevron-${idx + 1}-of-${chevronCount}`} transform={`translate(${pt.x}, ${pt.y}) rotate(${pt.angle})`}>
                            {/* Small clean chevron - same as movement */}
                            <path d="M-1.2,-1 L0,0 L-1.2,1" fill="none" stroke={playerColor} strokeWidth="0.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </g>
                        ))}
                        
                        {/* Step badge for screen move - at 40% along path, RIGHT of direction */}
                        {(() => {
                          const pathLength = getPathLength(offsetPoints);
                          const targetDist = pathLength * 0.4;
                          let badgePt = { x: offsetPoints[0].x, y: offsetPoints[0].y, angle: 0 };
                          let currentDist = 0;
                          
                          for (let i = 1; i < offsetPoints.length; i++) {
                            const dx = offsetPoints[i].x - offsetPoints[i - 1].x;
                            const dy = offsetPoints[i].y - offsetPoints[i - 1].y;
                            const segLen = Math.sqrt(dx * dx + dy * dy);
                            
                            if (currentDist + segLen >= targetDist) {
                              const t = (targetDist - currentDist) / segLen;
                              badgePt = {
                                x: offsetPoints[i - 1].x + dx * t,
                                y: offsetPoints[i - 1].y + dy * t,
                                angle: Math.atan2(dy, dx)
                              };
                              break;
                            }
                            currentDist += segLen;
                          }
                          
                          // Offset to RIGHT of direction (+π/2)
                          const perpAngle = badgePt.angle + Math.PI / 2;
                          const offsetDist = 3;
                          const badgeX = badgePt.x + Math.cos(perpAngle) * offsetDist;
                          const badgeY = badgePt.y + Math.sin(perpAngle) * offsetDist;
                          
                          return (
                            <g>
                              <circle cx={badgeX} cy={badgeY} r="2" fill="#1e3a5f" stroke="#fff" strokeWidth="0.3" />
                              <text x={badgeX} y={badgeY + 0.3} textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize="2" fontWeight="bold">{stepNum}</text>
                            </g>
                          );
                        })()}
                      </>
                    ) : (
                      <>
                        {/* White outline for visibility - thinner */}
                        <path d={d} fill="none" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" opacity="0.5" />
                        
                        {/* Main colored line - thin and clean */}
                        <path d={d} fill="none" stroke={playerColor} strokeWidth="0.6" strokeLinecap="round" strokeLinejoin="round" 
                          opacity="0.85"
                          style={{ pointerEvents: 'stroke', cursor: 'pointer' }} 
                          onClick={(e) => { e.stopPropagation(); onPathSelect?.(path.id); }} 
                        />
                        
                        {/* Path icons by type */}
                        {pathType === 'dribble' && chevronPositions.map((pt, idx) => (
                          <g key={`icon-${idx}`} className={`chevron-${idx + 1}-of-${chevronCount}`} transform={`translate(${pt.x}, ${pt.y})`}>
                            <text y="0.3" textAnchor="middle" dominantBaseline="middle" fontSize="3" style={{ filter: 'drop-shadow(0 0 1px #fff)' }}>{getBallEmoji(sport)}</text>
                          </g>
                        ))}
                        
                        {/* v0.5.21: Cut paths use double chevrons that rotate with path direction */}
                        {pathType === 'cut' && chevronPositions.map((pt, idx) => (
                          <g key={`chevron-${idx}`} className={`chevron-${idx + 1}-of-${chevronCount}`} transform={`translate(${pt.x}, ${pt.y}) rotate(${pt.angle})`}>
                            {/* Double chevron for cuts */}
                            <path d="M-1.8,-1.2 L-0.6,0 L-1.8,1.2" fill="none" stroke={playerColor} strokeWidth="0.6" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M-0.6,-1.2 L0.6,0 L-0.6,1.2" fill="none" stroke={playerColor} strokeWidth="0.6" strokeLinecap="round" strokeLinejoin="round"/>
                          </g>
                        ))}
                        
                        {pathType === 'movement' && chevronPositions.map((pt, idx) => (
                          <g key={`chevron-${idx}`} className={`chevron-${idx + 1}-of-${chevronCount}`} transform={`translate(${pt.x}, ${pt.y}) rotate(${pt.angle})`}>
                            {/* Small clean chevron */}
                            <path d="M-1.2,-1 L0,0 L-1.2,1" fill="none" stroke={playerColor} strokeWidth="0.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </g>
                        ))}
                        
                        {/* Step badge - at 40% along path, RIGHT of direction */}
                        {(() => {
                          // Find point at ~40% along the path
                          const pathLength = getPathLength(offsetPoints);
                          const targetDist = pathLength * 0.4;
                          let badgePt = { x: offsetPoints[0].x, y: offsetPoints[0].y, angle: 0 };
                          let currentDist = 0;
                          
                          for (let i = 1; i < offsetPoints.length; i++) {
                            const dx = offsetPoints[i].x - offsetPoints[i - 1].x;
                            const dy = offsetPoints[i].y - offsetPoints[i - 1].y;
                            const segLen = Math.sqrt(dx * dx + dy * dy);
                            
                            if (currentDist + segLen >= targetDist) {
                              const t = (targetDist - currentDist) / segLen;
                              badgePt = {
                                x: offsetPoints[i - 1].x + dx * t,
                                y: offsetPoints[i - 1].y + dy * t,
                                angle: Math.atan2(dy, dx)
                              };
                              break;
                            }
                            currentDist += segLen;
                          }
                          
                          // Offset to RIGHT of direction (+π/2)
                          const perpAngle = badgePt.angle + Math.PI / 2;
                          const offsetDist = 3;
                          const badgeX = badgePt.x + Math.cos(perpAngle) * offsetDist;
                          const badgeY = badgePt.y + Math.sin(perpAngle) * offsetDist;
                          
                          return (
                            <g>
                              <circle cx={badgeX} cy={badgeY} r="2" fill="#1e3a5f" stroke="#fff" strokeWidth="0.3" />
                              <text x={badgeX} y={badgeY + 0.3} textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize="2" fontWeight="bold">{stepNum}</text>
                            </g>
                          );
                        })()}
                      </>
                    )}
                    
                    {isSelected && path.points.map((pt, ptIdx) => (
                      <circle key={`${path.id}-pt-${ptIdx}`} cx={pt.x} cy={pt.y} r="1.2" fill={ptIdx === 0 || ptIdx === path.points.length - 1 ? playerColor : '#fff'} stroke={playerColor} strokeWidth="0.3" style={{ pointerEvents: 'auto', cursor: 'move' }} onMouseDown={(e) => { e.stopPropagation(); setDraggingPathPoint({ pathId: path.id, pointIndex: ptIdx }); }} />
                    ))}
                  </g>
                );
              })}
              
              {/* v0.5.72: Defender paths are NOT rendered visually to avoid confusing players
                  The path data still exists and controls defender movement during animation,
                  but coaches only see defenders at their positions, not the path lines */}
              
              {/* Pass Lines */}
              {validPasses.map((pass) => {
                const stepNum = pass.stepNumber || 1;
                
                // v0.5.64: Pass history visibility - works in both animation AND design mode
                let passOpacity = 0.85;
                let showPass = true;
                let showAnimatedBall = false;
                let ballProgress = 0;
                
                // Hide all passes at step 0 (initial positions view)
                if (currentStep === 0) {
                  showPass = false;
                } else {
                  // v0.5.64: Apply step-based visibility in BOTH animation and design mode
                  if (stepNum < currentStep) {
                    passOpacity = 0.3; // Previous steps - faded
                  } else if (stepNum === currentStep) {
                    passOpacity = 1.0; // Current - bright
                    if (isAnimating) {
                      showAnimatedBall = true; // Show ball moving during animation
                      ballProgress = animationProgress;
                    }
                  } else {
                    showPass = false; // Future - hidden
                  }
                }
                
                // v0.4.0: Spotlight mode
                if (spotlightPlayer && pass.fromPositionId !== spotlightPlayer && pass.toPositionId !== spotlightPlayer) {
                  passOpacity *= 0.25;
                }
                
                if (!showPass) return null;
                
                const fromPos = getPlayerPositionAtStep(pass.fromPositionId, stepNum - 1) || pass.from;
                const toPos = getPlayerPositionAtStep(pass.toPositionId, stepNum - 1) || pass.to;
                const d = pass.type === 'lob' ? `M ${fromPos.x},${fromPos.y} Q ${(fromPos.x + toPos.x) / 2},${Math.min(fromPos.y, toPos.y) - 8} ${toPos.x},${toPos.y}` : `M ${fromPos.x},${fromPos.y} L ${toPos.x},${toPos.y}`;
                const passer = getPlayerInfo(pass.fromPositionId);
                const passerColor = passer?.color || '#374151';
                
                // v0.5.0: Calculate animated ball position along pass
                const getPassBallPosition = (progress) => {
                  if (pass.type === 'lob') {
                    // Quadratic bezier for lob passes
                    const controlX = (fromPos.x + toPos.x) / 2;
                    const controlY = Math.min(fromPos.y, toPos.y) - 8;
                    const t = progress;
                    const x = (1 - t) * (1 - t) * fromPos.x + 2 * (1 - t) * t * controlX + t * t * toPos.x;
                    const y = (1 - t) * (1 - t) * fromPos.y + 2 * (1 - t) * t * controlY + t * t * toPos.y;
                    return { x, y };
                  } else {
                    // Linear for direct/bounce passes
                    return {
                      x: fromPos.x + (toPos.x - fromPos.x) * progress,
                      y: fromPos.y + (toPos.y - fromPos.y) * progress
                    };
                  }
                };
                
                return (
                  <g key={pass.id} opacity={passOpacity}>
                    <path d={d} fill="none" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" opacity="0.5" />
                    <path d={d} fill="none" stroke={passerColor} strokeWidth="0.6" strokeDasharray={pass.type === 'bounce' ? '2,2' : 'none'} strokeLinecap="round" markerEnd={`url(#${getArrowId(passerColor)})`} />
                    
                    {/* v0.5.0: Single animated basketball moving along pass during animation */}
                    {showAnimatedBall && (() => {
                      const ballPos = getPassBallPosition(ballProgress);
                      return (
                        <g transform={`translate(${ballPos.x}, ${ballPos.y})`}>
                          <circle r="2.5" fill="#f97316" stroke="#fff" strokeWidth="0.3" />
                          <text y="0.4" textAnchor="middle" dominantBaseline="middle" fontSize="3.5" style={{ filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.5))' }}>{getBallEmoji(sport)}</text>
                        </g>
                      );
                    })()}
                    
                    {/* Static indicators when not animating */}
                    {!isAnimating && (() => {
                      const midPoint = getPointOnLine(fromPos, toPos, 0.5);
                      return (
                        <g transform={`translate(${midPoint.x}, ${midPoint.y})`}>
                          <text y="0.3" textAnchor="middle" dominantBaseline="middle" fontSize="2.5" opacity="0.6">{getBallEmoji(sport)}</text>
                        </g>
                      );
                    })()}
                    
                    {/* Step badge - at 40% along pass line, RIGHT of direction */}
                    {(() => {
                      const dx = toPos.x - fromPos.x;
                      const dy = toPos.y - fromPos.y;
                      const midX = fromPos.x + dx * 0.4;
                      const midY = fromPos.y + dy * 0.4;
                      const angle = Math.atan2(dy, dx);
                      const perpAngle = angle + Math.PI / 2;
                      const offsetDist = 3;
                      const badgeX = midX + Math.cos(perpAngle) * offsetDist;
                      const badgeY = midY + Math.sin(perpAngle) * offsetDist;
                      
                      return (
                        <g>
                          <circle cx={badgeX} cy={badgeY} r="2" fill="#1e3a5f" stroke="#fff" strokeWidth="0.3" />
                          <text x={badgeX} y={badgeY + 0.3} textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize="2" fontWeight="bold">{stepNum}</text>
                        </g>
                      );
                    })()}
                  </g>
                );
              })}
              
              {/* Shot Trajectories */}
              {validShots.map((shot, idx) => {
                const stepNum = shot.stepNumber || 1;
                
                // v0.4.0: Shot history visibility
                // v0.5.89: Apply step-based visibility in BOTH animation and design mode
                let shotOpacity = 0.9;
                let showShot = true;
                let showAnimatedBall = false;
                let ballProgress = 0;
                
                // Hide all shots at step 0 (initial positions view)
                if (currentStep === 0) {
                  showShot = false;
                } else {
                  if (stepNum < currentStep) {
                    shotOpacity = 0.3; // Previous steps - faded
                  } else if (stepNum === currentStep) {
                    shotOpacity = 1.0; // Current step - bright
                    if (isAnimating) {
                      showAnimatedBall = true;
                      ballProgress = animationProgress;
                    }
                  } else {
                    showShot = false; // Future steps - hidden
                  }
                }
                
                // v0.4.0: Spotlight mode
                if (spotlightPlayer && shot.shooterId !== spotlightPlayer) {
                  shotOpacity *= 0.25;
                }
                
                if (!showShot) return null;
                
                const shooterPos = getPlayerPositionAtStep(shot.shooterId, stepNum - 1);
                if (!shooterPos) return null;
                
                // v0.5.97: Higher arc for shot trajectory (no visible line)
                const arcHeight = Math.abs(shooterPos.y - HOOP_POSITION.y) * 0.6 + 8;
                const controlX = (shooterPos.x + HOOP_POSITION.x) / 2;
                const controlY = Math.min(shooterPos.y, HOOP_POSITION.y) - arcHeight;
                
                // Calculate ball position along quadratic bezier
                const getShotBallPosition = (progress) => {
                  const t = progress;
                  const x = (1 - t) * (1 - t) * shooterPos.x + 2 * (1 - t) * t * controlX + t * t * HOOP_POSITION.x;
                  const y = (1 - t) * (1 - t) * shooterPos.y + 2 * (1 - t) * t * controlY + t * t * HOOP_POSITION.y;
                  return { x, y };
                };
                
                return (
                  <g key={shot.id || `shot-${idx}`} opacity={shotOpacity}>
                    {/* v0.5.97: No arc line - just the animated ball */}
                    
                    {/* Animated basketball moving along arc during animation */}
                    {showAnimatedBall && (() => {
                      const ballPos = getShotBallPosition(ballProgress);
                      // Ball gets smaller as it approaches hoop (perspective effect)
                      const ballScale = 1 - (ballProgress * 0.3);
                      return (
                        <g transform={`translate(${ballPos.x}, ${ballPos.y})`}>
                          <circle r={2.2 * ballScale} fill="#ea580c" stroke="#fff" strokeWidth="0.3" />
                          <text y="0.3" textAnchor="middle" dominantBaseline="middle" fontSize={3.2 * ballScale} style={{ filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.5))' }}>{getBallEmoji(sport)}</text>
                        </g>
                      );
                    })()}
                    
                    {/* Static ball at hoop when not animating (shot completed) */}
                    {!isAnimating && (
                      <g transform={`translate(${HOOP_POSITION.x}, ${HOOP_POSITION.y - 2})`}>
                        <circle r="1.8" fill="#ea580c" stroke="#fff" strokeWidth="0.2" opacity="0.8" />
                        <text y="0.2" textAnchor="middle" dominantBaseline="middle" fontSize="2.5" opacity="0.9">{getBallEmoji(sport)}</text>
                      </g>
                    )}
                    
                    {/* "Swish" effect at hoop when ball arrives */}
                    {showAnimatedBall && ballProgress > 0.85 && (
                      <g transform={`translate(${HOOP_POSITION.x}, ${HOOP_POSITION.y})`}>
                        <circle r={3 + (ballProgress - 0.85) * 20} fill="none" stroke="#f59e0b" strokeWidth="0.5" opacity={1 - (ballProgress - 0.85) * 6} />
                      </g>
                    )}
                    
                    {/* Step badge - positioned near shooter */}
                    {(() => {
                      const badgePos = getShotBallPosition(0.15);
                      const dx = HOOP_POSITION.x - shooterPos.x;
                      const dy = controlY - shooterPos.y;
                      const angle = Math.atan2(dy, dx);
                      const perpAngle = angle + Math.PI / 2;
                      const offsetDist = 3;
                      const badgeX = badgePos.x + Math.cos(perpAngle) * offsetDist;
                      const badgeY = badgePos.y + Math.sin(perpAngle) * offsetDist;
                      
                      return (
                        <g>
                          <circle cx={badgeX} cy={badgeY} r="2" fill="#1e3a5f" stroke="#fff" strokeWidth="0.3" />
                          <text x={badgeX} y={badgeY + 0.3} textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize="2" fontWeight="bold">{stepNum}</text>
                        </g>
                      );
                    })()}
                  </g>
                );
              })}
              
              {/* Screen Indicators - smaller, cleaner design */}
              {validScreens.map((screen, i) => {
                const setter = getPlayerInfo(screen.setterId);
                const setterColor = setter?.color || '#374151';
                const setterLabel = setter?.positionLabel || '?';
                const screenX = screen.x, screenY = screen.y;
                const rotation = screen.rotation || 0;
                // v0.5.75: Standard screen size - 120% width, 60% height
                const widthMult = 1.2;
                const heightMult = 0.6;
                const stepNum = screen.stepNumber || 1;
                
                // During animation: show screen from its step onward (persists until setter moves)
                // Check if setter has a path AFTER the screen step
                const setterRollPath = paths.find(p => 
                  p.positionId === screen.setterId && 
                  (p.stepNumber || 1) > stepNum &&
                  !p.isScreenMove
                );
                const rollStep = setterRollPath?.stepNumber || Infinity;
                
                // v0.5.64: Screen visibility - works in both animation AND design mode
                let screenOpacity = 1;
                let showScreen = true;
                
                // Check if there's a movement path to set this screen
                const screenMovePath = paths.find(p => 
                  p.positionId === screen.setterId && 
                  p.isScreenMove && 
                  (p.stepNumber || 1) === stepNum
                );
                
                // Hide all screens at step 0 (initial positions view)
                if (currentStep === 0) {
                  showScreen = false;
                } else if (currentStep < stepNum) {
                  showScreen = false; // Step not started yet (future)
                } else if (currentStep >= rollStep) {
                  showScreen = false; // Setter is rolling (screen no longer active)
                } else if (isAnimating && currentStep === stepNum && screenMovePath) {
                  // v0.5.0: During movement step animation, only show screen after arrival
                  if (animationProgress < 0.95) {
                    showScreen = false; // Still moving to screen location
                  } else {
                    screenOpacity = 0.5 + animationProgress * 0.5; // Fade in at end
                  }
                } else {
                  // v0.5.98: Screen stays fully dark/visible while in place
                  // (both current step and past steps until setter rolls)
                  screenOpacity = 1.0;
                }
                
                // v0.4.0: Spotlight mode - dim non-spotlit screen setters
                if (spotlightPlayer && screen.setterId !== spotlightPlayer) {
                  screenOpacity *= 0.25;
                }
                
                if (!showScreen) return null;
                
                // Smaller base dimensions
                const barW = 10 * widthMult;
                const barH = 4 * heightMult;
                
                // Check if this screen setter is selected
                const isScreenSelected = selectedPosition === screen.setterId;
                
                return (
                  <g key={screen.id || `screen-${i}`} opacity={screenOpacity}>
                    {/* v0.4.6: Screen bar - draggable */}
                    <g 
                      transform={`translate(${screenX}, ${screenY}) rotate(${rotation})`}
                      style={{ cursor: 'move', pointerEvents: 'auto' }}
                      onMouseDown={(e) => { e.stopPropagation(); setDraggingScreen(screen.id); }}
                      onTouchStart={(e) => { e.stopPropagation(); setDraggingScreen(screen.id); }}
                    >
                      {/* Screen bar - sharp or rounded based on shape property */}
                      <rect 
                        x={-barW/2} 
                        y={-barH/2} 
                        width={barW} 
                        height={barH} 
                        rx={(screen.shape || 'sharp') === 'rounded' ? 1.5 : 0} 
                        fill={setterColor} 
                        stroke="#fff" 
                        strokeWidth="0.3"
                      />
                    </g>
                    
                    {/* Player icon on screen bar - drag to move screen, click to select */}
                    <g 
                      style={{ cursor: 'move', pointerEvents: 'auto' }}
                      onMouseDown={(e) => { 
                        e.stopPropagation(); 
                        setDraggingScreen(screen.id); 
                      }}
                      onTouchStart={(e) => { 
                        e.stopPropagation(); 
                        setDraggingScreen(screen.id); 
                      }}
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        // Only select on click (not drag)
                        if (!draggingScreen) {
                          const setterPos = positions.find(p => 
                            p.id === screen.setterId || 
                            p.positionLabel === screen.setterId ||
                            p.id === setter?.id
                          );
                          if (setterPos && onPlayerClick) {
                            onPlayerClick({ ...setterPos, screenLocation: { x: screenX, y: screenY }, fromScreen: screen.id });
                          }
                        }
                      }}
                    >
                      <circle 
                        cx={screenX} 
                        cy={screenY} 
                        r="2" 
                        fill={setterColor}
                        stroke={isScreenSelected ? '#374151' : '#fff'} 
                        strokeWidth={isScreenSelected ? '0.8' : '0.4'}
                        strokeDasharray={isScreenSelected ? '1,0.5' : 'none'}
                      />
                      <text x={screenX} y={screenY + 0.3} textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize="1.8" fontWeight="bold" style={{ pointerEvents: 'none' }}>{setterLabel}</text>
                    </g>
                    
                    {/* Show player at roll end location (in design mode) */}
                    {!isAnimating && setterRollPath && (() => {
                      const rollEnd = setterRollPath.points[setterRollPath.points.length - 1];
                      return (
                        <g>
                          <circle 
                            cx={rollEnd.x} 
                            cy={rollEnd.y} 
                            r={BASE_RADIUS} 
                            fill={setterColor}
                            stroke="#fff"
                            strokeWidth="0.4"
                          />
                          <text x={rollEnd.x} y={rollEnd.y + 0.4} textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize="2.8" fontWeight="bold" style={{ pointerEvents: 'none' }}>{setterLabel}</text>
                        </g>
                      );
                    })()}
                  </g>
                );
              })}
              
              {/* Currently Drawing */}
              {isDrawingPath && currentPath.length > 1 && (() => {
                // v0.5.71: Support both player and defender path drawing
                const isDefenderPath = drawingMode?.type === 'defenderPath';
                const player = isDefenderPath ? null : getPlayerInfo(drawingMode?.positionId);
                const pathColor = isDefenderPath ? '#dc2626' : (player?.color || '#374151');
                return (
                  <>
                    <path d={smoothPath(currentPath)} fill="none" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" opacity="0.7" />
                    <path d={smoothPath(currentPath)} fill="none" stroke={pathColor} strokeWidth="0.6" strokeLinecap="round" strokeLinejoin="round" markerEnd={`url(#${getArrowId(pathColor)})`} />
                  </>
                );
              })()}
              
              {/* Players - ALWAYS visible at their current positions */}
              {placedPositions.map((pos) => {
                const r = BASE_RADIUS * (pos.size || 1);
                const isSelected = selectedPosition === pos.id;
                const isDrawingFrom = drawingMode?.positionId === pos.id;
                const hasBall = pos.id === currentBallHolder;
                
                // Calculate player position based on current animation state
                let playerX, playerY;
                
                // v0.5.85: BULLETPROOF step 0 check - ALWAYS use startX/startY
                if (currentStep === 0) {
                  playerX = pos.startX;
                  playerY = pos.startY;
                  console.log(`[STEP0] ${pos.positionLabel} at startX=${pos.startX}, startY=${pos.startY}`);
                } else {
                  // Check if this player is a screen setter (only matters for steps > 0)
                  const playerScreen = validScreens.find(s => 
                    s.setterId === pos.id || 
                    s.setterId === pos.positionLabel ||
                    (positions.find(p => p.id === s.setterId)?.positionLabel === pos.positionLabel)
                  );
                
                  if (playerScreen) {
                    const screenStep = playerScreen.stepNumber || 1;
                  
                  // Check if there's a screen move path for this setter
                  const screenMovePath = paths.find(p => 
                    p.positionId === pos.id && 
                    p.isScreenMove && 
                    (p.stepNumber || 1) === screenStep
                  );
                  
                  // Check if setter has a roll path after the screen
                  const rollPath = paths.find(p => 
                    p.positionId === pos.id && 
                    (p.stepNumber || 1) > screenStep &&
                    !p.isScreenMove
                  );
                  const rollStep = rollPath?.stepNumber || Infinity;
                  
                  // v0.5.20: Apply player-specific speed
                  const setterSpeed = pos.speed || 1.0;
                  const setterAdjustedProgress = Math.min(1, animationProgress * setterSpeed);
                  
                  // v0.5.47: Step 0 is handled above, so here we're at step > 0
                  if (isAnimating) {
                    if (currentStep < screenStep) {
                      // Before screen step: use normal animated position
                      const animPos = getAnimatedPosition(pos);
                      if (!animPos) return null;
                      playerX = animPos.x;
                      playerY = animPos.y;
                    } else if (currentStep === screenStep && screenMovePath) {
                      // v0.5.17: During screen step with move path - animate along the path
                      const pts = screenMovePath.points;
                      if (pts && pts.length >= 2) {
                        // v0.5.20: Use player-adjusted progress
                        const idx = setterAdjustedProgress * (pts.length - 1);
                        const lo = Math.floor(idx);
                        const hi = Math.min(lo + 1, pts.length - 1);
                        const t = idx - lo;
                        playerX = pts[lo].x + (pts[hi].x - pts[lo].x) * t;
                        playerY = pts[lo].y + (pts[hi].y - pts[lo].y) * t;
                      } else {
                        playerX = playerScreen.x;
                        playerY = playerScreen.y;
                      }
                    } else if (currentStep >= screenStep && currentStep < rollStep) {
                      // After screen step but before roll: at screen location
                      playerX = playerScreen.x;
                      playerY = playerScreen.y;
                    } else if (currentStep === rollStep && rollPath) {
                      // At roll step: animate along roll path
                      const pts = rollPath.points;
                      // v0.5.20: Use player-adjusted progress
                      const idx = setterAdjustedProgress * (pts.length - 1);
                      const lo = Math.floor(idx);
                      const hi = Math.min(lo + 1, pts.length - 1);
                      const t = idx - lo;
                      playerX = pts[lo].x + (pts[hi].x - pts[lo].x) * t;
                      playerY = pts[lo].y + (pts[hi].y - pts[lo].y) * t;
                    } else if (currentStep > rollStep && rollPath) {
                      // After roll step: at roll end
                      const rollEnd = rollPath.points[rollPath.points.length - 1];
                      playerX = rollEnd.x;
                      playerY = rollEnd.y;
                    } else {
                      // Fallback: at screen location
                      playerX = playerScreen.x;
                      playerY = playerScreen.y;
                    }
                  } else {
                    // Design mode (not animating): show at position based on currentStep
                    // v0.5.85: Use getAnimatedPosition which handles step logic
                    const animPos = getAnimatedPosition(pos);
                    if (animPos) {
                      playerX = animPos.x;
                      playerY = animPos.y;
                    } else {
                      playerX = playerScreen.x;
                      playerY = playerScreen.y;
                    }
                  }
                  
                  // v0.5.17: Hide standalone player when screen bar is visible (screen bar has its own icon)
                  const screenIsVisible = isAnimating && (
                    (currentStep === screenStep && animationProgress >= 0.95) ||
                    (currentStep > screenStep && currentStep < rollStep)
                  );
                  if (screenIsVisible && !isSelected) {
                    return null; // Screen bar shows the player icon
                  }
                } else {
                  // Non-screen setter: use normal animated position
                  const animPos = getAnimatedPosition(pos);
                  if (!animPos) return null;
                  playerX = animPos.x;
                  playerY = animPos.y;
                }
                } // end of else (currentStep > 0)
                
                // v0.5.98: Check if player has any actions in the play
                const hasActions = 
                  paths.some(p => p.positionId === pos.id) ||
                  passes.some(p => p.fromPositionId === pos.id || p.toPositionId === pos.id) ||
                  screens.some(s => s.setterId === pos.id) ||
                  (shots || []).some(s => s.shooterId === pos.id);
                
                // v0.4.0: Spotlight mode - calculate player opacity
                const isSpotlit = spotlightPlayer === pos.id;
                const isWatermark = spotlightPlayer && !isSpotlit;
                // v0.5.98: Fade players without actions (unless spotlit)
                let playerOpacity = 1;
                if (isWatermark) {
                  playerOpacity = 0.25;
                } else if (!hasActions && !isSpotlit) {
                  playerOpacity = 0.4; // Faded for players with no actions
                }
                
                // Ball position: touching edge at 45°
                const ballOffsetX = r * 0.707 + 1.0;
                const ballOffsetY = -(r * 0.707 + 1.0);
                
                return (
                  <g key={pos.id} opacity={playerOpacity}>
                    <g 
                      className={hasBall ? 'ball-holder' : ''}
                      style={{ transformOrigin: `${playerX}px ${playerY}px`, cursor: 'pointer', pointerEvents: 'auto' }}
                      transform={`rotate(${pos.rotation || 0}, ${playerX}, ${playerY})`} 
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        onPlayerClick?.(pos);
                        // v0.4.2: Toggle spotlight on player click (same as focus row)
                        if (onSpotlightChange) {
                          onSpotlightChange(spotlightPlayer === pos.id ? null : pos.id);
                        }
                      }} 
                      onMouseDown={(e) => { e.stopPropagation(); setDraggingPlayer(pos.id); }} 
                      onTouchStart={(e) => { e.stopPropagation(); setDraggingPlayer(pos.id); }}
                    >
                      {/* v0.4.3: Spotlight ring - thinner border */}
                      {isSpotlit && (
                        <circle cx={playerX} cy={playerY} r={r + 0.8} fill="none" stroke="#fbbf24" strokeWidth="0.4" opacity="0.9" />
                      )}
                      
                      {/* v0.5.71: Jersey shape with template colors, position color outline */}
                      {/* v0.6.0: Only show jersey shapes for basketball; other sports use colored circles */}
                      {showJerseys && sport === 'basketball' ? (
                        <>
                          {/* Jersey sleeveless shape */}
                          <path
                            d={`M ${playerX - r * 1.1} ${playerY - r * 1.2}
                                L ${playerX - r * 0.7} ${playerY - r * 1.5}
                                L ${playerX - r * 0.3} ${playerY - r * 1.3}
                                L ${playerX} ${playerY - r * 1.4}
                                L ${playerX + r * 0.3} ${playerY - r * 1.3}
                                L ${playerX + r * 0.7} ${playerY - r * 1.5}
                                L ${playerX + r * 1.1} ${playerY - r * 1.2}
                                L ${playerX + r * 1.0} ${playerY + r * 1.3}
                                L ${playerX - r * 1.0} ${playerY + r * 1.3}
                                Z`}
                            fill={jerseyTemplate?.fillColor || '#1e40af'}
                            stroke={pos.color}
                            strokeWidth={(isSelected || isDrawingFrom) ? '0.6' : '0.4'}
                            strokeDasharray={isDrawingFrom ? '1.5,1' : 'none'}
                          />
                          {/* Collar detail */}
                          <ellipse
                            cx={playerX}
                            cy={playerY - r * 1.25}
                            rx={r * 0.5}
                            ry={r * 0.25}
                            fill="none"
                            stroke={pos.color}
                            strokeWidth="0.25"
                            opacity="0.7"
                          />
                        </>
                      ) : (
                        <circle
                          cx={playerX} cy={playerY} r={r}
                          fill={pos.color}
                          stroke={(isSelected || isDrawingFrom) ? '#374151' : pos.color}
                          strokeWidth={(isSelected || isDrawingFrom) ? '0.6' : '0.5'}
                          strokeDasharray={isDrawingFrom ? '1.5,1' : 'none'}
                        />
                      )}

                      <text x={playerX} y={playerY + ((showJerseys && sport === 'basketball') ? 0 : 0.3)} textAnchor="middle" dominantBaseline="middle" fill={(showJerseys && sport === 'basketball') ? (jerseyTemplate?.numberColor || '#fff') : '#fff'} fontSize={2.2 * (pos.size || 1)} fontWeight="bold" style={{ pointerEvents: 'none' }}>{pos.jerseyNumber || pos.positionLabel}</text>
                      {hasBall && (
                        <g transform={`translate(${playerX + ballOffsetX}, ${playerY + ballOffsetY})`}>
                          <text y="0.3" textAnchor="middle" dominantBaseline="middle" fontSize="3" style={{ filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.5))' }}>{getBallEmoji(sport)}</text>
                        </g>
                      )}
                    </g>
                  </g>
                );
              })}
              
              {/* Defenders - v0.4.6: Animated positions based on paths or following guarded player */}
              {defenders.map((def, idx) => {
                const defSize = def.size || 0.6;
                const defRadius = 3 * defSize;
                
                // v0.5.45: Check both guardingId and guardingPlayerId for backwards compatibility
                let guardingPlayer = (def.guardingId || def.guardingPlayerId)
                  ? positions.find(p => p.id === (def.guardingId || def.guardingPlayerId))
                  : null;
                
                // If guardingId/guardingPlayerId doesn't match, try index matching as fallback
                if (!guardingPlayer && positions[idx]) {
                  guardingPlayer = positions[idx];
                }
                
                // v0.5.11: Show position label (PG, SG) - always show the position being guarded
                const displayLabel = guardingPlayer?.positionLabel || def.label || `D${idx + 1}`;
                const guardingColor = def.labelColor || guardingPlayer?.color || '#fff';
                
                // v0.5.62: Defender outline color matches guarded player's color
                const defenderOutlineColor = guardingPlayer?.color || '#7f1d1d';
                
                // v0.5.70: Defender position - ONLY moves via explicit defender paths
                // "Guarding" assignment is for label/color ONLY, not auto-following
                let defX = def.x;
                let defY = def.y;
                
                // Check for explicit defender paths
                const defenderPathsForThis = defenderPaths.filter(p => p.defenderId === def.id);
                
                if (defenderPathsForThis.length > 0) {
                  // Apply explicit defender paths
                  let currentDefX = def.x;
                  let currentDefY = def.y;
                  
                  if (!isAnimating) {
                    // Design mode: use end position of paths up to current step
                    defenderPathsForThis
                      .filter(p => (p.stepNumber || 1) <= currentStep)
                      .sort((a, b) => (a.stepNumber || 1) - (b.stepNumber || 1))
                      .forEach(path => {
                        if (path.points?.length >= 2) {
                          const lastPoint = path.points[path.points.length - 1];
                          currentDefX = lastPoint.x;
                          currentDefY = lastPoint.y;
                        }
                      });
                    defX = currentDefX;
                    defY = currentDefY;
                  } else {
                    // Animation mode: interpolate along current path
                    const defenderSpeed = def.speed || 1.0;
                    const defAdjustedProgress = Math.min(1, animationProgress * defenderSpeed);
                    
                    defenderPathsForThis
                      .filter(p => (p.stepNumber || 1) < currentStep)
                      .sort((a, b) => (a.stepNumber || 1) - (b.stepNumber || 1))
                      .forEach(path => {
                        if (path.points?.length >= 2) {
                          const lastPoint = path.points[path.points.length - 1];
                          currentDefX = lastPoint.x;
                          currentDefY = lastPoint.y;
                        }
                      });
                    
                    // Check if there's a path for current step being animated
                    const currentDefPath = defenderPathsForThis.find(p => (p.stepNumber || 1) === currentStep);
                    if (currentDefPath && currentDefPath.points?.length >= 2) {
                      const fromPoint = currentDefPath.points[0];
                      const toPoint = currentDefPath.points[currentDefPath.points.length - 1];
                      defX = fromPoint.x + (toPoint.x - fromPoint.x) * defAdjustedProgress;
                      defY = fromPoint.y + (toPoint.y - fromPoint.y) * defAdjustedProgress;
                    } else {
                      defX = currentDefX;
                      defY = currentDefY;
                    }
                  }
                }
                // v0.5.70: If no explicit paths, defender stays at initial position (def.x, def.y)
                
                const isDefSelected = selectedDefender === def.id;
                
                return (
                  <g key={def.id || `defender-${idx}`}
                    style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                    onClick={(e) => { e.stopPropagation(); onDefenderClick?.(def); }}
                    onMouseDown={(e) => { e.stopPropagation(); setDraggingDefender(def.id); }}
                    onTouchStart={(e) => { e.stopPropagation(); setDraggingDefender(def.id); }}
                  >
                    {/* v0.5.71: Selection ring for selected defender */}
                    {isDefSelected && (
                      <circle cx={defX} cy={defY} r={defRadius + 1} fill="none" stroke="#fbbf24" strokeWidth="0.5" opacity="0.9" />
                    )}
                    
                    {/* v0.5.71: Defender jersey shape or circle */}
                    {/* v0.6.0: Only show jersey shapes for basketball; other sports use colored circles */}
                    {showJerseys && sport === 'basketball' ? (
                      <>
                        {/* Jersey sleeveless shape */}
                        <path
                          d={`M ${defX - defRadius * 1.1} ${defY - defRadius * 1.2}
                              L ${defX - defRadius * 0.7} ${defY - defRadius * 1.5}
                              L ${defX - defRadius * 0.3} ${defY - defRadius * 1.3}
                              L ${defX} ${defY - defRadius * 1.4}
                              L ${defX + defRadius * 0.3} ${defY - defRadius * 1.3}
                              L ${defX + defRadius * 0.7} ${defY - defRadius * 1.5}
                              L ${defX + defRadius * 1.1} ${defY - defRadius * 1.2}
                              L ${defX + defRadius * 1.0} ${defY + defRadius * 1.3}
                              L ${defX - defRadius * 1.0} ${defY + defRadius * 1.3}
                              Z`}
                          fill={defenderJerseyColor}
                          stroke={defenderOutlineColor}
                          strokeWidth={isDefSelected ? '0.6' : '0.4'}
                        />
                        {/* Collar detail */}
                        <ellipse
                          cx={defX}
                          cy={defY - defRadius * 1.25}
                          rx={defRadius * 0.5}
                          ry={defRadius * 0.25}
                          fill="none"
                          stroke={defenderOutlineColor}
                          strokeWidth="0.25"
                          opacity="0.7"
                        />
                      </>
                    ) : (
                      <circle
                        cx={defX}
                        cy={defY}
                        r={defRadius}
                        fill={defenderJerseyColor}
                        stroke={defenderOutlineColor}
                        strokeWidth={isDefSelected ? '1' : '0.8'}
                      />
                    )}
                    
                    {/* v0.5.10: Always show label (position label if guarding, or D1/D2 etc) */}
                    <>
                      {/* Black stroke/outline behind text */}
                      <text 
                        x={defX} 
                        y={defY + ((showJerseys && sport === 'basketball') ? 0 : 0.3)}
                        textAnchor="middle" 
                        dominantBaseline="middle" 
                        fill="none"
                        stroke="#000"
                        strokeWidth="0.6"
                        fontSize={displayLabel.length > 2 ? 1.8 * defSize : 2.2 * defSize} 
                        fontWeight="bold"
                        style={{ pointerEvents: 'none' }}
                      >
                        {displayLabel}
                      </text>
                      {/* White fill on top */}
                      <text 
                        x={defX} 
                        y={defY + ((showJerseys && sport === 'basketball') ? 0 : 0.3)}
                        textAnchor="middle" 
                        dominantBaseline="middle" 
                        fill="#fff"
                        fontSize={displayLabel.length > 2 ? 1.8 * defSize : 2.2 * defSize} 
                        fontWeight="bold"
                        style={{ pointerEvents: 'none' }}
                      >
                        {displayLabel}
                      </text>
                    </>
                  </g>
                );
              })}
              
              {/* Placement cursor */}
              {placementMode && cursorPosition && (
                <g opacity="0.7">
                  <circle cx={cursorPosition.x} cy={cursorPosition.y} r="4" fill={hexToRgba(placementMode.color || '#2563eb', 0.7)} stroke={placementMode.color || '#2563eb'} strokeWidth="1" />
                  <text x={cursorPosition.x} y={cursorPosition.y + 0.3} textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize="3" fontWeight="bold" style={{ pointerEvents: 'none' }}>{placementMode.label}</text>
                </g>
              )}
              
              {/* Tap mode cursor */}
              {tapMode && cursorPosition && (
                <g opacity="0.8">
                  <circle cx={cursorPosition.x} cy={cursorPosition.y} r="4" fill="none" stroke={tapMode.type === 'dribble' ? '#ea580c' : tapMode.type === 'cut' ? '#dc2626' : '#2563eb'} strokeWidth="0.8" strokeDasharray="2,2" />
                  <circle cx={cursorPosition.x} cy={cursorPosition.y} r="1" fill={tapMode.type === 'dribble' ? '#ea580c' : tapMode.type === 'cut' ? '#dc2626' : '#2563eb'} />
                  <line x1={cursorPosition.x - 2.5} y1={cursorPosition.y} x2={cursorPosition.x + 2.5} y2={cursorPosition.y} stroke={tapMode.type === 'dribble' ? '#ea580c' : tapMode.type === 'cut' ? '#dc2626' : '#2563eb'} strokeWidth="0.5" />
                  <line x1={cursorPosition.x} y1={cursorPosition.y - 2.5} x2={cursorPosition.x} y2={cursorPosition.y + 2.5} stroke={tapMode.type === 'dribble' ? '#ea580c' : tapMode.type === 'cut' ? '#dc2626' : '#2563eb'} strokeWidth="0.5" />
                </g>
              )}
              
              {drawingMode && cursorPosition && !isDrawingPath && <circle cx={cursorPosition.x} cy={cursorPosition.y} r="1.5" fill="none" stroke="#374151" strokeWidth="0.5" strokeDasharray="2,1" opacity="0.8" />}
            </svg>
          )}
        </div>
      </div>
    </div>
  );
};

export default BasketballCourt;
